import { Link } from 'react-router-dom';
import { useSEO } from '../../utils/seo';

export default function AgencesPage() {
  // SEO Configuration
  useSEO({
    title: 'Agences de Communication Événementielle - Designer PowerPoint Partenaire | Izislide',
    description: 'Imagez vos idées : Designer PowerPoint expert pour agences de communication événementielle. Création de présentations PowerPoint professionnelles pour vos clients. Livraison rapide, qualité garantie.',
    keywords: 'designer powerpoint, agence communication événementielle, création présentation powerpoint, sous-traitance ppt, design powerpoint agence, présentation client',
    canonical: '/agences',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Designer PowerPoint pour Agences de Communication Événementielle',
      description: 'Service de création de présentations PowerPoint professionnelles pour agences de communication événementielle',
      provider: {
        '@type': 'Organization',
        name: 'Izislide',
        slogan: 'Imagez vos idées'
      },
      areaServed: 'FR',
      serviceType: 'Création de présentations PowerPoint'
    }
  });

  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section 
        className="relative pt-32 pb-24 overflow-hidden bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(0,168,107,0.85) 0%, rgba(0,143,92,0.85) 100%), url('https://public.readdy.ai/ai/img_res/5451cc723bb89bbb83162f5d2fb5cd77.jpg')`
        }}
      >
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Agences de Communication Événementielle : Offrez à vos clients des présentations PowerPoint au niveau de vos campagnes
            </h1>
            <p className="text-xl md:text-2xl mb-10 opacity-90">
              <strong>Imagez vos idées</strong> : Un designer PowerPoint partenaire fiable pour sublimer vos présentations clients
            </p>
            <a 
              href="#solution" 
              onClick={(e) => scrollToSection(e, 'solution')}
              className="inline-block bg-white text-[#00A86B] px-10 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Je veux un partenaire PowerPoint pour mon agence
            </a>
          </div>
        </div>
      </section>

      {/* Pain Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
              Le défi des agences de communication événementielle
            </h2>
            <div className="bg-white p-10 rounded-xl shadow-lg border-l-4 border-red-400">
              <div className="space-y-6 text-lg text-gray-700">
                <p className="flex items-start">
                  <i className="ri-close-circle-line text-2xl text-red-500 mr-4 mt-1"></i>
                  <span>Vos équipes créatives excellent en stratégie et campagnes, mais les <strong>présentations PowerPoint clients</strong> prennent un temps fou</span>
                </p>
                <p className="flex items-start">
                  <i className="ri-close-circle-line text-2xl text-red-500 mr-4 mt-1"></i>
                  <span>Les <strong>urgences de dernière minute</strong> pour des PPT clients perturbent vos plannings</span>
                </p>
                <p className="flex items-start">
                  <i className="ri-close-circle-line text-2xl text-red-500 mr-4 mt-1"></i>
                  <span>Difficile de maintenir une <strong>cohérence graphique PowerPoint</strong> entre tous vos projets événementiels</span>
                </p>
                <p className="flex items-start">
                  <i className="ri-close-circle-line text-2xl text-red-500 mr-4 mt-1"></i>
                  <span>Vos talents créatifs perdent du temps sur des <strong>tâches de design PowerPoint</strong> au lieu de se concentrer sur la stratégie</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Agitate Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
              Et si vous pouviez vous concentrer sur votre cœur de métier ?
            </h2>
            <p className="text-xl text-gray-700 mb-6">
              Imaginez : plus de stress pour les <strong>présentations PowerPoint clients</strong>, plus de nuits blanches avant les pitchs événementiels, plus de compromis sur la qualité des PPT.
            </p>
            <p className="text-xl text-gray-700">
              Juste un <strong>designer PowerPoint partenaire</strong> qui comprend vos enjeux d'agence de communication événementielle et livre des présentations impeccables, dans les délais.
            </p>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section id="solution" className="py-20 bg-[#C9D86A]/10">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-12 text-center">
              Izislide : Votre designer PowerPoint dédié aux agences de communication événementielle
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8 mb-16">
              <div className="bg-white p-8 rounded-xl shadow-lg">
                <div className="w-16 h-16 bg-[#00A86B]/10 rounded-lg flex items-center justify-center mb-6">
                  <i className="ri-time-line text-3xl text-[#00A86B]"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Gain de temps massif</h3>
                <p className="text-gray-700">
                  Déléguez vos <strong>présentations PowerPoint</strong> et récupérez jusqu'à <strong>15h par semaine</strong> pour vos projets événementiels stratégiques
                </p>
              </div>

              <div className="bg-white p-8 rounded-xl shadow-lg">
                <div className="w-16 h-16 bg-[#F7A348]/10 rounded-lg flex items-center justify-center mb-6">
                  <i className="ri-palette-line text-3xl text-[#F7A348]"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Cohérence graphique PowerPoint</h3>
                <p className="text-gray-700">
                  Toutes vos <strong>présentations PPT clients</strong> respectent votre charte graphique et vos standards de qualité événementielle
                </p>
              </div>

              <div className="bg-white p-8 rounded-xl shadow-lg">
                <div className="w-16 h-16 bg-[#00A86B]/10 rounded-lg flex items-center justify-center mb-6">
                  <i className="ri-flashlight-line text-3xl text-[#00A86B]"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Urgences maîtrisées</h3>
                <p className="text-gray-700">
                  Livraison express <strong>sous 48h</strong> pour vos présentations PowerPoint urgentes. Votre <strong>designer PowerPoint</strong> réactif
                </p>
              </div>
            </div>

            <div className="bg-white p-10 rounded-xl shadow-xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                Comment ça marche pour votre agence de communication événementielle ?
              </h3>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-[#F7A348] rounded-full flex items-center justify-center text-white font-bold text-xl mr-6 flex-shrink-0">
                    1
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">Vous nous briefez sur votre présentation PowerPoint client</h4>
                    <p className="text-gray-700">Objectifs événementiels, messages clés, charte graphique - 30 minutes suffisent</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-[#00A86B] rounded-full flex items-center justify-center text-white font-bold text-xl mr-6 flex-shrink-0">
                    2
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">Notre designer PowerPoint crée votre PPT</h4>
                    <p className="text-gray-700"><strong>Imagez vos idées</strong> : design impactant, storytelling événementiel, messages clairs</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-[#C9D86A] rounded-full flex items-center justify-center text-gray-900 font-bold text-xl mr-6 flex-shrink-0">
                    3
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">Livraison et ajustements</h4>
                    <p className="text-gray-700">Présentation PowerPoint livrée sous 48-72h, 2 allers-retours inclus</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            Des agences de communication événementielle qui nous font confiance
          </h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-gray-50 p-8 rounded-xl">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-[#00A86B] rounded-full flex items-center justify-center text-white font-bold text-xl">
                  ML
                </div>
                <div className="ml-4">
                  <div className="font-bold text-gray-900">Marc Lefebvre</div>
                  <div className="text-sm text-gray-600">Directeur Créatif, Agence Événementielle</div>
                </div>
              </div>
              <p className="text-gray-700 italic mb-4">
                "Izislide est devenu notre <strong>designer PowerPoint</strong> de référence. Leurs présentations PPT subliment nos concepts événementiels. Nos clients sont bluffés à chaque fois."
              </p>
              <div className="flex text-[#F7A348]">
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
              </div>
            </div>

            <div className="bg-gray-50 p-8 rounded-xl">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-[#F7A348] rounded-full flex items-center justify-center text-white font-bold text-xl">
                  CB
                </div>
                <div className="ml-4">
                  <div className="font-bold text-gray-900">Camille Bernard</div>
                  <div className="text-sm text-gray-600">CEO, Agence Communication</div>
                </div>
              </div>
              <p className="text-gray-700 italic mb-4">
                "Depuis qu'on travaille avec Izislide, on a gagné <strong>20h par semaine</strong> sur nos présentations PowerPoint. On peut enfin se concentrer sur nos événements. <strong>Imagez vos idées</strong>, c'est exactement ce qu'ils font !"
              </p>
              <div className="flex text-[#F7A348]">
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
              Questions fréquentes des agences de communication événementielle
            </h2>
            <div className="space-y-6">
              <details className="bg-white p-6 rounded-xl shadow-md cursor-pointer">
                <summary className="font-bold text-lg text-gray-900 cursor-pointer">
                  Pouvez-vous respecter notre charte graphique dans les présentations PowerPoint ?
                </summary>
                <p className="mt-4 text-gray-700">
                  Absolument ! Notre <strong>designer PowerPoint</strong> s'adapte à 100% à votre identité visuelle. Nous créons même des templates PowerPoint personnalisés pour votre agence de communication événementielle.
                </p>
              </details>

              <details className="bg-white p-6 rounded-xl shadow-md cursor-pointer">
                <summary className="font-bold text-lg text-gray-900 cursor-pointer">
                  Quels sont vos délais de livraison pour les présentations PPT ?
                </summary>
                <p className="mt-4 text-gray-700">
                  Standard : 48-72h. Express : 24h. Pour vos urgences événementielles, nous proposons même un service <strong>same-day</strong> pour les présentations PowerPoint critiques.
                </p>
              </details>

              <details className="bg-white p-6 rounded-xl shadow-md cursor-pointer">
                <summary className="font-bold text-lg text-gray-900 cursor-pointer">
                  Proposez-vous un forfait mensuel pour agences de communication événementielle ?
                </summary>
                <p className="mt-4 text-gray-700">
                  Oui ! Nous avons des <strong>forfaits de sous-traitance PowerPoint</strong> adaptés aux agences : de 5 à 20 présentations PPT par mois, avec designer PowerPoint dédié et tarifs préférentiels.
                </p>
              </details>

              <details className="bg-white p-6 rounded-xl shadow-md cursor-pointer">
                <summary className="font-bold text-lg text-gray-900 cursor-pointer">
                  Gardez-vous la confidentialité de nos projets événementiels clients ?
                </summary>
                <p className="mt-4 text-gray-700">
                  Confidentialité totale garantie. NDA systématique pour toutes vos <strong>présentations PowerPoint clients</strong>. Vos projets événementiels restent 100% confidentiels.
                </p>
              </details>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-24 bg-gradient-to-r from-[#00A86B] to-[#008f5c]">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Prêt à déléguer vos présentations PowerPoint ?
            </h2>
            <p className="text-xl mb-10 opacity-90">
              Discutons de votre besoin et devenez partenaire avec notre <strong>designer PowerPoint</strong> expert
            </p>
            <Link 
              to="/contact" 
              className="inline-block bg-white text-[#00A86B] px-10 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Je veux un partenaire PowerPoint pour mon agence
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}